/**
 * Configurator price bridge.
 *
 * The Pixlip product configurator widget computes the configured bundle price
 * and dispatches a `productPrice:change` CustomEvent on `document`, whose
 * `detail` is `{ status: 'pending' }`, `{ status: 'ready', amountCents }`, or
 * `{ status: 'error' }`. This drives the theme's existing `<price-list>` price
 * displays accordingly — a spinner while a price is resolving, the formatted
 * amount when it lands.
 *
 * It's loaded on every product page and simply listens for the event: when no
 * configurator is present the event never fires and this is an inert no-op, so
 * there's no metafield gate to get wrong. It only wires anything up when a
 * configurator IS present (`#gsconfigurator-container`) — otherwise a variant
 * re-render on an ordinary product would trip the observer below and leave a
 * spinner on a price that never resolves.
 *
 * It deliberately does NOT register `price-list` as a custom element. Custom
 * elements are single-definition, so claiming the tag name would either break
 * (if the theme ever defines `price-list` first, our behavior never attaches) or
 * make the theme break (if we define it first, the theme's later define throws).
 * Instead we attach behavior externally: a `document` listener updates the price
 * nodes on each event, and a MutationObserver re-applies the last price to any
 * node a `<product-rerender>` swaps in. That stays correct no matter what the
 * theme does with the `price-list` tag.
 *
 * The same amount also drives the VAT notice block, which restates the price
 * with tax (snippets/vat-notice.liquid). It is scoped and re-applied exactly
 * like the price displays, and carries the rate plus its localized string as
 * data attributes so this file needs no VAT wording of its own.
 *
 * Only the configured product's price displays react: the main product price and
 * the sticky buy bar. Both live inside a `<product-rerender>` observing the
 * configured product's form - but so do two kinds of price block belonging to
 * OTHER products, so `product-rerender price-list` alone is far too broad and
 * would stamp the configured bundle price onto them. `priceNodes()` below
 * documents the two filters that exclude them. The latest price is cached by
 * the widget on `window.__gsConfiguratorPrice`, so a late load or a re-rendered
 * node can re-apply the configured price instead of reverting to the
 * placeholder.
 */
(function () {
  var STYLE_ID = "gs-configurator-price-style";
  var LOADING_ATTR = "data-gs-price-loading";

  function injectSpinnerStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = [
      // Keep the price visible but dimmed while resolving; overlay a spinner
      // centered over it. Centering uses negative margins (not transform) so it
      // doesn't fight the rotation animation's `transform`.
      "price-list[" + LOADING_ATTR + "]{position:relative;}",
      "price-list[" + LOADING_ATTR + "] > *{opacity:0.4;}",
      "price-list[" +
        LOADING_ATTR +
        "]::after{content:'';position:absolute;top:50%;left:50%;width:1em;height:1em;margin:-0.5em 0 0 -0.5em;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;animation:gs-price-spin .6s linear infinite;}",
      // The VAT notice restates the same amount, so it dims alongside the price
      // rather than sitting there stating a stale gross figure.
      ".vat-notice[" + LOADING_ATTR + "]{opacity:0.4;}",
      "@keyframes gs-price-spin{to{transform:rotate(360deg);}}",
    ].join("");
    document.head.appendChild(style);
  }

  // Standard Shopify money formatter — reproduces the Liquid `money` /
  // `money_with_currency` filters for a given amount of cents and format string.
  function formatMoney(cents, format) {
    var value = "";
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = typeof precision === "undefined" ? 2 : precision;
      thousands = typeof thousands === "undefined" ? "," : thousands;
      decimal = typeof decimal === "undefined" ? "." : decimal;
      if (isNaN(number) || number == null) return "0";
      number = (number / 100.0).toFixed(precision);
      var parts = number.split(".");
      var dollars = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        "$1" + thousands,
      );
      var centsPart = parts[1] ? decimal + parts[1] : "";
      return dollars + centsPart;
    }

    var match = format.match(placeholderRegex);
    var placeholder = match ? match[1] : "amount";
    switch (placeholder) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 2, "'", ".");
        break;
      case "amount_no_decimals_with_space_separator":
        value = formatWithDelimiters(cents, 0, " ", "");
        break;
      case "amount_with_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ",");
        break;
      case "amount_with_period_and_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ".");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return format.replace(placeholderRegex, value);
  }

  function moneyFormatString() {
    var settings =
      (window.themeVariables && window.themeVariables.settings) || {};
    var format = settings.currencyCodeEnabled
      ? settings.moneyWithCurrencyFormat
      : settings.moneyFormat;
    return format || "${{amount}}";
  }

  function isConfiguratorPage() {
    return !!document.getElementById("gsconfigurator-container");
  }

  // The form id of the configured product, read off the <product-rerender> in
  // the configurator's own section. Both price hosts we care about - the main
  // price block (sections/main-product.liquid) and the sticky buy bar - observe
  // this form, and every foreign price block observes a different one, so this
  // is what tells "our" prices apart from someone else's.
  function configuredFormId() {
    var container = document.getElementById("gsconfigurator-container");
    var section = container && container.closest(".shopify-section");
    var host = section && section.querySelector("product-rerender[observe-form]");
    return host ? host.getAttribute("observe-form") : null;
  }

  // The price displays we drive: main product price + sticky bar. Two filters,
  // because two different kinds of foreign price block sit inside a
  // <product-rerender> on this page:
  //
  //   - A complementary product's quick-buy drawer renders a whole
  //     <product-rerender id="quick-buy-modal-content"> for THAT product
  //     (snippets/product-quick-buy.liquid), fetched from its own PDP. It is
  //     excluded by observe-form, which points at the other product's form.
  //   - The complementary cards themselves are nested inside our own main
  //     <product-rerender>, so observe-form does not separate them. They carry
  //     a `data-product-handle` root (snippets/horizontal-product.liquid) and
  //     our main/sticky price blocks do not, so that attribute does.
  //
  // Product-card, cart and related prices live outside <product-rerender>
  // entirely and were never matched.
  function priceNodes() {
    var formId = configuredFormId();
    if (!formId) return [];
    return Array.prototype.slice
      .call(
        document.querySelectorAll(
          'product-rerender[observe-form="' + formId + '"] price-list',
        ),
      )
      .filter(function (node) {
        return !node.closest("[data-product-handle]");
      });
  }

  function currentDetail() {
    return window.__gsConfiguratorPrice || { status: "pending" };
  }

  function renderAmount(priceListNode, amountCents) {
    if (typeof amountCents !== "number" || !isFinite(amountCents)) return;
    var salePrice = priceListNode.querySelector("sale-price");
    if (!salePrice) return;
    var srOnly = salePrice.querySelector(".sr-only");
    var formatted = formatMoney(amountCents, moneyFormatString());
    salePrice.innerHTML = (srOnly ? srOnly.outerHTML : "") + formatted;
  }

  function applyDetailTo(priceListNode, detail) {
    if (!detail || typeof detail !== "object") return;
    if (detail.status === "pending") {
      priceListNode.setAttribute(LOADING_ATTR, "");
      return;
    }
    // "error" just drops the spinner and leaves whatever price is shown (the
    // last configured amount, or the server-rendered placeholder).
    priceListNode.removeAttribute(LOADING_ATTR);
    if (detail.status !== "ready") return;
    renderAmount(priceListNode, detail.amountCents);
  }

  // The VAT notice (snippets/vat-notice.liquid) states the tax counterpart of
  // the price, so the configured amount has to reach it too, and it is scoped
  // exactly like the price displays for the same reasons. It carries the rate,
  // the tax direction and its localized string (price left as a placeholder) as
  // data attributes, so this bridge never has to know VAT wording or how a
  // given language orders the rate against the amount.
  var VAT_PRICE_PLACEHOLDER = "__VAT_PRICE__";

  function vatNodes() {
    var formId = configuredFormId();
    if (!formId) return [];
    return Array.prototype.slice
      .call(
        document.querySelectorAll(
          'product-rerender[observe-form="' +
            formId +
            '"] .vat-notice[data-vat-template]',
        ),
      )
      .filter(function (node) {
        return !node.closest("[data-product-handle]");
      });
  }

  function renderVat(vatNode, amountCents) {
    if (typeof amountCents !== "number" || !isFinite(amountCents)) return;
    var target = vatNode.querySelector("[data-vat-text]");
    var template = vatNode.getAttribute("data-vat-template");
    var rate = parseFloat(vatNode.getAttribute("data-vat-rate"));
    if (!target || !template || !isFinite(rate)) return;
    // Mirrors the Liquid: the gross price when shop prices exclude tax, the net
    // price when they include it.
    var multiplier = rate + 100;
    var converted =
      vatNode.getAttribute("data-vat-taxes-included") === "true"
        ? (amountCents * 100) / multiplier
        : (amountCents * multiplier) / 100;
    target.textContent = template.replace(
      VAT_PRICE_PLACEHOLDER,
      formatMoney(Math.round(converted), moneyFormatString()),
    );
  }

  function applyDetailToVat(vatNode, detail) {
    if (!detail || typeof detail !== "object") return;
    if (detail.status === "pending") {
      vatNode.setAttribute(LOADING_ATTR, "");
      return;
    }
    vatNode.removeAttribute(LOADING_ATTR);
    if (detail.status !== "ready") return;
    renderVat(vatNode, detail.amountCents);
  }

  function applyToAll(detail) {
    var nodes = priceNodes();
    for (var i = 0; i < nodes.length; i++) applyDetailTo(nodes[i], detail);
    var vats = vatNodes();
    for (var j = 0; j < vats.length; j++) applyDetailToVat(vats[j], detail);
  }

  // A <product-rerender> can replace its price block with a fresh, server-
  // rendered node (variant re-render / partial re-render), which reverts it to
  // the placeholder price. Re-apply the last known price whenever a new
  // price-list or VAT notice node appears. We react only to ADDED elements of
  // those two kinds — our own writes add text/spans inside <sale-price> and set
  // textContent on an existing [data-vat-text] span, never adding a price-list
  // or a .vat-notice, so this can't loop.
  function observeReRenders() {
    if (typeof MutationObserver === "undefined") return;
    var containers = document.querySelectorAll("product-rerender");
    if (!containers.length) return;
    var observer = new MutationObserver(function (mutations) {
      var sawNewPrice = false;
      for (var i = 0; i < mutations.length && !sawNewPrice; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (
            (node.matches && node.matches("price-list, .vat-notice")) ||
            (node.querySelector && node.querySelector("price-list, .vat-notice"))
          ) {
            sawNewPrice = true;
            break;
          }
        }
      }
      if (sawNewPrice) applyToAll(currentDetail());
    });
    for (var k = 0; k < containers.length; k++) {
      observer.observe(containers[k], { childList: true, subtree: true });
    }
  }

  function init() {
    if (!isConfiguratorPage()) return;
    injectSpinnerStyle();
    document.addEventListener("productPrice:change", function (event) {
      applyToAll(event.detail);
    });
    // `productHardware:change` carries the hardware variant's PDP fields and no
    // price, so it cannot drive an amount on its own. It is still worth
    // listening to: a hardware swap rewrites parts of the PDP, and re-applying
    // the cached price keeps the price and VAT displays consistent with it
    // without waiting for the next `productPrice:change`.
    document.addEventListener("productHardware:change", function () {
      applyToAll(currentDetail());
    });
    // Apply whatever the widget has emitted so far (or the pending spinner).
    applyToAll(currentDetail());
    observeReRenders();
  }

  // `defer` guarantees the DOM is parsed, but guard readyState anyway.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
